import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Download, RefreshCw, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ProgramsApi, programKeys } from "@/api/perkpoint/programs";
import { apiErrorMessage } from "@/api/perkpoint/client";
import {
  InsightsApi,
  changeText,
  compact,
  insightKeys,
  monthLabel,
  saveBlob,
  type InsightDays,
} from "@/api/perkpoint/insights";
import { useActiveProgramId, usePerkPointScope } from "./programs/program-scope";
import { ChartExportButton } from "./chart-controls";

const SOURCE_META: Record<string, { label: string; color: string }> = {
  base: { label: "Base earn rule", color: "bg-green-600" },
  bonus: { label: "Bonus rules", color: "bg-emerald-400" },
  campaign: { label: "Campaigns", color: "bg-amber-500" },
  manual: { label: "Manual adjustments", color: "bg-slate-400" },
};
const TIER_COLORS = ["bg-orange-500", "bg-slate-400", "bg-amber-500", "bg-sky-600", "bg-violet-600"];

function toneFor(value: number | null | undefined, invert = false) {
  if (value == null || value === 0) return "text-muted-foreground";
  return (value > 0) !== invert ? "text-green-700" : "text-orange-600";
}

function whenText(iso: string) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 864e5);
  if (days <= 0) {
    const hours = Math.floor((Date.now() - new Date(iso).getTime()) / 36e5);
    return hours <= 0 ? "just now" : `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  if (days === 1) return "yesterday";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function PerkPointDashboard() {
  const [period, setPeriod] = useState("30");
  const queryClient = useQueryClient();
  const { mode, session } = usePerkPointScope();
  const { programId } = useActiveProgramId();
  const days = Number(period) as InsightDays;
  const dashboardQuery = useQuery({
    queryKey: insightKeys.dashboard(programId ?? "", days),
    queryFn: () => InsightsApi.dashboard(programId!, days),
    enabled: Boolean(programId),
  });
  const dash = dashboardQuery.data;
  const programQuery = useQuery({
    queryKey: programKeys.detail(programId ?? ""),
    queryFn: () => ProgramsApi.get(programId!),
    enabled: Boolean(programId),
  });
  const programName =
    programQuery.data?.name ?? (mode === "manager" && session ? session.programName : "this program");

  const refreshMutation = useMutation({
    mutationFn: () => InsightsApi.refresh(programId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: insightKeys.all });
      toast.success("Dashboard numbers recalculated");
    },
    onError: (error) => toast.error(apiErrorMessage(error, "Could not refresh the numbers")),
  });
  const exportCsv = async () => {
    try {
      const blob = await InsightsApi.exportDashboard(programId!, days);
      saveBlob(blob, `dashboard-${days}d-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not export the dashboard"));
    }
  };

  const sum = dash?.summary;
  const money = (value: number) =>
    `${(sum?.currency ?? "").trim()} ${compact(Math.round(value))}`.trim();
  const SUMMARY_METRICS = sum
    ? [
        {
          label: "Total members",
          value: sum.total_members.toLocaleString("en-IN"),
          meta: `${changeText(sum.total_members_change_pct)} · ${sum.new_this_month.toLocaleString("en-IN")} new this month`,
          metaClass: toneFor(sum.total_members_change_pct),
        },
        {
          label: "Active members",
          value: sum.active_members.toLocaleString("en-IN"),
          meta: `${sum.active_share_pct == null ? "—" : `${sum.active_share_pct}%`} of total · ${changeText(sum.active_change_pts, " pts")}`,
          metaClass: "text-muted-foreground",
        },
        {
          label: "Points issued",
          value: compact(sum.points_issued),
          meta: `${changeText(sum.points_issued_change_pct)} vs prior period`,
          metaClass: toneFor(sum.points_issued_change_pct),
        },
        {
          label: "Points redeemed",
          value: compact(sum.points_redeemed),
          meta: `${sum.redeemed_share_of_issued_pct == null ? "—" : `${sum.redeemed_share_of_issued_pct}%`} of issued`,
          metaClass: "text-muted-foreground",
        },
        {
          label: "Outstanding liability",
          value: money(sum.outstanding_liability),
          meta: `CPP ${sum.cpp} · ${changeText(sum.liability_change_pct)}`,
          metaClass: toneFor(sum.liability_change_pct, true),
        },
        {
          label: "Campaigns live",
          value: String(sum.campaigns_live),
          meta: `${sum.campaigns_ending_this_week} ending this week`,
          metaClass: "text-muted-foreground",
        },
      ]
    : [];
  const ENROLMENT_DATA = (dash?.enrolments_by_month ?? []).map((row) => ({
    month: monthLabel(row.month),
    value: row.value,
  }));
  const latestMonth = ENROLMENT_DATA[ENROLMENT_DATA.length - 1]?.month;
  const enrolmentRange = dash?.enrolments_by_month.length
    ? `${monthLabel(dash.enrolments_by_month[0].month)} – ${monthLabel(
        dash.enrolments_by_month[dash.enrolments_by_month.length - 1].month,
      )} ${dash.enrolments_by_month[dash.enrolments_by_month.length - 1].month.slice(0, 4)}`
    : "";
  const POINTS_SOURCES = (dash?.points_by_source ?? []).map((row) => ({
    label: SOURCE_META[row.source]?.label ?? row.source,
    value: compact(row.points),
    color: SOURCE_META[row.source]?.color ?? "bg-slate-300",
  }));
  const TIERS = (dash?.tiers ?? []).map((tier, index) => ({
    name: tier.name,
    count: tier.count.toLocaleString("en-IN"),
    percent: `${tier.percent}%`,
    change: `+${tier.upgrades} / -${tier.downgrades}`,
    color: TIER_COLORS[Math.min(index, TIER_COLORS.length - 1)],
    bar: `${tier.percent}%`,
  }));
  const maxSegment = Math.max(1, ...(dash?.segments ?? []).map((s) => s.count));
  const SEGMENTS = (dash?.segments ?? []).map((segment) => ({
    name: segment.name,
    count: segment.count.toLocaleString("en-IN"),
    pct: Math.round((segment.count / maxSegment) * 100),
    change: changeText(segment.change_pct),
    up: (segment.change_pct ?? 0) >= 0,
  }));
  const ATTENTION_ITEMS = (dash?.needs_attention ?? []).map((item, index) => ({
    id: `${item.tag}-${index}`,
    ...item,
  }));
  const CAMPAIGN_PERFORMANCE = (dash?.campaign_performance ?? []).map((row) => ({
    id: row.campaign_id,
    campaign: row.campaign,
    channel: row.channel,
    sent: row.sent.toLocaleString("en-IN"),
    read: row.read == null ? "—" : row.read.toLocaleString("en-IN"),
    converted: row.converted == null ? "—" : row.converted.toLocaleString("en-IN"),
    lift: row.lift_pts == null ? "—" : changeText(row.lift_pts, " pts"),
    liftTone: (row.lift_pts ?? 0) >= 3 ? ("high" as const) : ("low" as const),
  }));
  const CONFIG_CHANGES = (dash?.recent_config_changes ?? []).map((item, index) => ({
    id: `${item.at}-${index}`,
    action: item.action,
    meta: [item.actor ?? "System", item.role, whenText(item.at)].filter(Boolean).join(" · "),
  }));

  return (
    <div className="space-y-6 pb-2">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-sm text-slate-700">Dashboard</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Program dashboard
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground">
            Members, points economy and program health across {programName} ·
            last {period === "7" ? "7" : period === "90" ? "90" : "30"} days.
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-2 sm:items-end">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {dash ? (
              <span
                className={cn(
                  "rounded-full px-2.5 py-1 text-xs font-medium",
                  dash.stale ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-600",
                )}
                title={`Snapshot ${dash.snapshot_date}`}
              >
                {dash.stale ? "Out of date · " : "Updated "}
                {new Date(dash.computed_at).toLocaleString("en-GB", {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-slate-600"
              disabled={!programId || refreshMutation.isPending}
              onClick={() => refreshMutation.mutate()}
            >
              <RefreshCw className={cn("mr-1 h-3.5 w-3.5", refreshMutation.isPending && "animate-spin")} />
              Refresh
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Period</span>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="h-9 w-[150px] bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {mode === "manager" && session ? (
              <Button asChild variant="outline" className="bg-white">
                <Link to={`/perkpoint/program/setup/${session.programId}`}>
                  <Settings className="mr-1.5 h-4 w-4" />
                  Configure program
                </Link>
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              className="bg-white"
              disabled={!programId || !dash}
              onClick={() => void exportCsv()}
            >
              <Download className="mr-1.5 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>
      </div>

      {!dash ? (
        <p className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-muted-foreground">
          {dashboardQuery.isError
            ? apiErrorMessage(dashboardQuery.error, "Could not load the dashboard")
            : "Loading dashboard…"}
        </p>
      ) : null}

      {/* Summary metrics */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        {SUMMARY_METRICS.map((metric) => (
          <Card key={metric.label} className="border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {metric.label}
              </p>
              <p className="mt-2 text-2xl font-bold tracking-tight text-slate-900">
                {metric.value}
              </p>
              <p className={cn("mt-1.5 text-xs", metric.metaClass)}>
                {metric.meta}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-slate-900">
                New enrolments by month
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {enrolmentRange}
                </span>
                <ChartExportButton
                  filename={`enrolments-${days}d.csv`}
                  rows={(dash?.enrolments_by_month ?? []).map((row) => ({
                    Month: monthLabel(row.month),
                    Enrolments: row.value,
                  }))}
                />
              </div>
            </div>
            <div className="h-[260px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ENROLMENT_DATA} barSize={36}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#e2e8f0"
                  />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#64748b", fontSize: 12 }}
                  />
                  <YAxis hide />
                  <Tooltip
                    cursor={{ fill: "rgba(34,197,94,0.06)" }}
                    formatter={(value: number) => [
                      value.toLocaleString("en-IN"),
                      "Enrolments",
                    ]}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#16a34a">
                    {ENROLMENT_DATA.map((entry) => (
                      <Cell
                        key={entry.month}
                        fill={
                          entry.month === latestMonth ? "#15803d" : "#22c55e"
                        }
                      />
                    ))}
                    <LabelList
                      dataKey="value"
                      position="top"
                      formatter={(value: number) =>
                        value >= 1000
                          ? `${(value / 1000).toFixed(1)}k`
                          : value
                      }
                      style={{ fill: "#64748b", fontSize: 11 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {sum
                ? `${sum.new_this_month.toLocaleString("en-IN")} members joined this month.`
                : ""}
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-slate-900">
                Points economy
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Last {period === "7" ? "7" : period === "90" ? "90" : "30"} days
                </span>
                <ChartExportButton
                  filename={`points-economy-${days}d.csv`}
                  rows={
                    sum
                      ? [
                          { Metric: "Issued", Value: sum.points_issued },
                          { Metric: "Redeemed", Value: sum.points_redeemed },
                          { Metric: "Outstanding", Value: sum.outstanding_points },
                          { Metric: "Liability", Value: sum.outstanding_liability },
                          ...(dash?.points_by_source ?? []).map((row) => ({
                            Metric: SOURCE_META[row.source]?.label ?? row.source,
                            Value: row.points,
                          })),
                        ]
                      : []
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                ["Issued", sum ? compact(sum.points_issued) : "—"],
                ["Redeemed", sum ? compact(sum.points_redeemed) : "—"],
                ["Outstanding", sum ? compact(sum.outstanding_points) : "—"],
                ["Liability", sum ? money(sum.outstanding_liability) : "—"],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="rounded-xl border border-slate-200 bg-slate-50/70 p-3"
                >
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="mt-1 text-xl font-bold text-slate-900">
                    {value}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-5">
              <p className="mb-3 text-sm font-medium text-slate-800">
                Where issued points came from
              </p>
              <div className="space-y-2.5">
                {POINTS_SOURCES.map((source) => (
                  <div
                    key={source.label}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={cn("h-2.5 w-2.5 rounded-full", source.color)}
                      />
                      <span className="text-slate-600">{source.label}</span>
                    </div>
                    <span className="font-semibold text-slate-900">
                      {source.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom row */}
      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-900">
                Members by tier
              </h2>
              <div className="flex items-center gap-2">
                <ChartExportButton
                  filename={`tiers-${days}d.csv`}
                  rows={(dash?.tiers ?? []).map((tier) => ({
                    Tier: tier.name,
                    Members: tier.count,
                    Percent: tier.percent,
                    Upgrades: tier.upgrades,
                    Downgrades: tier.downgrades,
                  }))}
                />
                <Link
                  to="/perkpoint/rewards/tiers"
                  className="text-sm font-medium text-green-700 hover:text-green-800"
                >
                  Configure tiers
                </Link>
              </div>
            </div>

            <div className="mb-4 flex h-2.5 overflow-hidden rounded-full bg-slate-100">
              {TIERS.map((tier) => (
                <div
                  key={tier.name}
                  className={tier.color}
                  style={{ width: tier.bar }}
                  title={`${tier.name} ${tier.percent}`}
                />
              ))}
            </div>

            <div className="space-y-3">
              {TIERS.map((tier) => (
                <div
                  key={tier.name}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className={cn("h-2.5 w-2.5 shrink-0 rounded-sm", tier.color)}
                    />
                    <span className="font-medium text-slate-800">
                      {tier.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-right">
                    <span className="font-semibold text-slate-900">
                      {tier.count}
                    </span>
                    <span className="w-8 text-muted-foreground">
                      {tier.percent}
                    </span>
                    <span className="hidden rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 sm:inline-flex">
                      {tier.change}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-xl border border-green-100 bg-green-50 px-3 py-2.5 text-sm text-green-800">
              3,412 members are within 20% of the next tier
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-900">
                Lifecycle segments
              </h2>
              <div className="flex items-center gap-2">
                <ChartExportButton
                  filename={`segments-${days}d.csv`}
                  rows={(dash?.segments ?? []).map((segment) => ({
                    Segment: segment.name,
                    Members: segment.count,
                    "Change %": segment.change_pct,
                  }))}
                />
                <Link
                  to="/perkpoint/member/segments"
                  className="text-sm font-medium text-green-700 hover:text-green-800"
                >
                  View segments
                </Link>
              </div>
            </div>
            <div className="space-y-4">
              {SEGMENTS.map((segment) => (
                <div key={segment.name} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium text-slate-800">
                      {segment.name}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">
                        {segment.count}
                      </span>
                      <span
                        className={cn(
                          "text-xs font-medium",
                          segment.up ? "text-green-700" : "text-red-600",
                        )}
                      >
                        {segment.change}
                      </span>
                    </div>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-green-600"
                      style={{ width: `${segment.pct}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-900">
                Needs attention
              </h2>
              <span className="text-sm text-muted-foreground">
                {ATTENTION_ITEMS.length} items
              </span>
            </div>
            <div className="space-y-3">
              {ATTENTION_ITEMS.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 rounded-xl border border-slate-100 p-3"
                >
                  <span
                    className={cn(
                      "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold",
                      item.tagClass,
                    )}
                  >
                    {item.tag}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900">
                      {item.title}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaign performance + recent changes */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-900">
                Campaign performance
              </h2>
              <Link
                to="/perkpoint/rewards/campaigns"
                className="text-sm font-medium text-green-700 hover:text-green-800"
              >
                All campaigns
              </Link>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Campaign
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Channel
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Sent
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Read
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Converted
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Lift vs control
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {CAMPAIGN_PERFORMANCE.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium text-slate-900">
                        {row.campaign}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                          {row.channel}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-700">{row.sent}</TableCell>
                      <TableCell className="text-slate-700">{row.read}</TableCell>
                      <TableCell className="text-slate-700">
                        {row.converted}
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            "inline-flex rounded-md px-2 py-1 text-xs font-semibold",
                            row.liftTone === "high"
                              ? "bg-green-50 text-green-700"
                              : "bg-orange-50 text-orange-700",
                          )}
                        >
                          {row.lift}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-900">
                Recent configuration changes
              </h2>
              <Link
                to="/perkpoint/audit/history"
                className="text-sm font-medium text-green-700 hover:text-green-800"
              >
                Audit trail
              </Link>
            </div>
            <div className="divide-y divide-slate-100">
              {CONFIG_CHANGES.map((item) => (
                <div key={item.id} className="py-3 first:pt-0 last:pb-0">
                  <p className="text-sm font-medium text-slate-900">
                    {item.action}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {item.meta}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
